import React, { FunctionComponent, useState, useCallback, useEffect } from 'react';
import { Grid, Box } from '@material-ui/core';
import moment from 'moment';
import AppointmentData from './Tabs/AppointmentData';
import Loader from '../../components/Loader';
import TabPanel from '../../components/TabPanel';
import AppointmentPersons from './Tabs/AppointmentPersons';
import AppointmentLicensesTab from './Tabs/AppointmentLicenses';
import AppointmentComment from './Tabs/AppointmentComments';
import Title from '../../components/PageHeader/Title';
import Header from '../../components/PageHeader/Header';
import PageNotFound from '../../components/PageNotFound';
import DetailPageComponent from '../../components/DetailPageComponent';
import {
    APPOINTMENT_DETAIL_TAB_LIST,
    APPOINTMENT_INSPECTION_DETAIL_TAB_LIST,
    INSPECTION_EVENT_TYPE,
} from '../../store/app.constants';
import {
    Form,
    Appointment,
    Application,
    InspectionList,
    License,
    ConfiguredFields,
} from '../../store/modules/application/types';
import { Person } from '../../store/modules/person/types';
import { Order } from '../../store/modules/application/types';
import useAuthenticationContext from '../../authentication/useAuthenticationContext';
import { Api } from '../../store/services/services';
import Inspection from '../LicenseDetailTabs/Tabs/Inspection';

type AppointmentDetailTabsProps = {
    match: { params: { id: string } };
    getAppointmentDetails: Function;
    isLoading: boolean;
    editForm: Form;
    appointmentDetail: Appointment;
    setActiveTab: Function;
    data: any;
    appointmentLicenses: Application[];
    appointmentLicensesLoading: boolean;
    getAppointmentCommentsList: Function;
    appointmentCommentList: any[];
    addAppointmentComment: Function;
    appointmentInspectionsList: InspectionList;
    appointmentInspectionsLoading: boolean;
    getAppointmentInspectionList: Function;
    hasMoreAppointmentInspections: boolean;
    addAppointmentInspection: Function;
    deleteAppoinmentInspection: Function;
    hasMoreComment: boolean;
    isAddAppointmentCommentLoading: boolean;
    getUserList: Function;
    getLinkedLicensesForAppointments: Function;
    personLicenses: License[];
    personLicensesLoadingStatus: boolean;
    setAppointmentPersonList: Function;
    addLicensesForAppointmentsBegin: Function;
    getFilteredLicensesForAppointments: Function;
    removeLicenseFromAppointment: Function;
    appointmentLicenseColumnFields: ConfiguredFields;
    getAppointmentTypeList: Function;
};
const AppointmentDetailTabs: FunctionComponent<AppointmentDetailTabsProps> = (props) => {
    const {
        getAppointmentDetails,
        isLoading,
        editForm,
        appointmentDetail,
        setActiveTab,
        appointmentLicenses,
        appointmentLicensesLoading,
        getAppointmentCommentsList,
        appointmentCommentList,
        addAppointmentComment,
        appointmentInspectionsList,
        appointmentInspectionsLoading,
        getAppointmentInspectionList,
        hasMoreAppointmentInspections,
        addAppointmentInspection,
        deleteAppoinmentInspection,
        hasMoreComment,
        isAddAppointmentCommentLoading,
        getUserList,
        getLinkedLicensesForAppointments,
        addLicensesForAppointmentsBegin,
        getFilteredLicensesForAppointments,
        personLicenses,
        personLicensesLoadingStatus,
        setAppointmentPersonList,
        removeLicenseFromAppointment,
        appointmentLicenseColumnFields,
        getAppointmentTypeList,
    } = props;
    const [tabList, setTabList] = useState<{ id: number; name: string }[]>();
    const [personList, setPersonList] = useState<Person[]>([]);
    const appointmentId = props.match.params.id;
    const authContext = useAuthenticationContext();

    const [skipComment, setSkipComment] = useState<number>(0);
    const [skipInspection, setSkipInspection] = useState<number>(0);
    const commentLimit = 20;
    const inspectionLimit = 20;
    const [appointmentInspectionSortOrder, setAppointmentInspectionSortOrder] = useState<Order>('desc');
    const [appointmentInspectionSortColumn, setAppointmentInspectionSortColumn] = useState<string>('inspectionDate');
    const [appointmentLicenseSortOrder, setAppointmentLicenseSortOrder] = useState<Order>('desc');
    const [appointmentLicenseSortColumn, setAppointmentLicenseSortColumn] = useState<string>('issueDate');
    const [appointmentPersonSortOrder, setAppointmentPersonSortOrder] = useState<Order>('desc');
    const [appointmentPersonSortColumn, setAppointmentPersonSortColumn] = useState<string>('name');

    useEffect(() => {
        getUserList();
    }, [getUserList]);

    useEffect(() => {
        getAppointmentDetails(appointmentId);
    }, [getAppointmentDetails, appointmentId, getAppointmentInspectionList]);

    useEffect(() => {
        getAppointmentCommentsList(appointmentId, { skip: skipComment, limit: commentLimit });
    }, [getAppointmentCommentsList, skipComment, appointmentId]);

    useEffect(() => {
        getAppointmentInspectionList(appointmentId, {
            limit: inspectionLimit,
            skip: skipInspection,
            orderCol: appointmentInspectionSortColumn,
            orderBy: appointmentInspectionSortOrder,
        });
    }, [
        getAppointmentInspectionList,
        appointmentId,
        skipInspection,
        appointmentInspectionSortColumn,
        appointmentInspectionSortOrder,
    ]);

    const getPersonList = useCallback(async () => {
        const token = await authContext
            .getAccessToken({ scopes: [authContext.scopes['persons.read']] })
            .then((response) => response.accessToken);

        const persons = appointmentDetail.persons?.map((person) => person.id);
        if (persons) {
            let result = await Api.getPersonList(token, persons);
            setAppointmentPersonList(result);
            setPersonList(result);
        }
        // eslint-disable-next-line
    }, [appointmentDetail.persons]);

    useEffect(() => {
        getPersonList();
    }, [getPersonList]);

    useEffect(() => {
        if (appointmentDetail.type?.id === INSPECTION_EVENT_TYPE) {
            setTabList(APPOINTMENT_INSPECTION_DETAIL_TAB_LIST);
        } else {
            setTabList(APPOINTMENT_DETAIL_TAB_LIST);
        }
    }, [appointmentDetail.type]);

    useEffect(() => {
        getAppointmentTypeList();
    }, [getAppointmentTypeList]);

    const onTabSelect = (tabName: string) => {
        setActiveTab(tabName);
    };

    const linkedLicenses = appointmentLicenses?.map((element: any) => {
        return {
            id: element?.id,
            type: element?.type?.name,
            licenseNumber: element?.licenseNumber,
            status: element?.status,
            expirationDate: element?.expirationDate,
            issueDate: element?.issueDate,
            lastInspection: element?.lastInspection,
            additionalData: element?.additionalData,
        };
    });

    const updateInspectionSortBy = (sortBy: string) => {
        setAppointmentInspectionSortColumn(sortBy);
        setSkipInspection(0);
    };

    const updateInspectionSortOrder = (sortOrder: Order) => {
        setAppointmentInspectionSortOrder(sortOrder);
        setSkipInspection(0);
    };

    const updateAppointmentLicenseSortOrder = (sortOrder: Order) => {
        setAppointmentLicenseSortOrder(sortOrder);
    };

    const updateAppointmentLicenseSortBy = (sortBy: string) => {
        setAppointmentLicenseSortColumn(sortBy);
    };

    const updateAppointmentPersonSortOrder = (sortOrder: Order) => {
        setAppointmentPersonSortOrder(sortOrder);
    };

    const updateAppointmentPersonSortBy = (sortBy: string) => {
        setAppointmentPersonSortColumn(sortBy);
    };

    const renderTabsPanel = (activeTab: number) => {
        const tab = tabList && tabList[activeTab];
        switch (tab?.id) {
            case 0:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <AppointmentData appointmentDetail={appointmentDetail} editForm={editForm} />
                    </TabPanel>
                );
            case 1:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <AppointmentPersons
                            persons={personList}
                            sortBy={appointmentPersonSortColumn}
                            sortOrder={appointmentPersonSortOrder}
                            setSortOrder={updateAppointmentPersonSortOrder}
                            setSortColumn={updateAppointmentPersonSortBy}
                        />
                    </TabPanel>
                );

            case 2:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <AppointmentLicensesTab
                            personList={personList}
                            licenses={linkedLicenses}
                            appointmentId={appointmentId}
                            personLicenses={personLicenses}
                            sortBy={appointmentLicenseSortColumn}
                            sortOrder={appointmentLicenseSortOrder}
                            setSortColumn={updateAppointmentLicenseSortBy}
                            setSortOrder={updateAppointmentLicenseSortOrder}
                            appointmentLicensesLoading={appointmentLicensesLoading}
                            addLicensesForAppointments={addLicensesForAppointmentsBegin}
                            personLicensesLoadingStatus={personLicensesLoadingStatus}
                            getLinkedLicensesForAppointments={getLinkedLicensesForAppointments}
                            getFilteredLicensesForAppointments={getFilteredLicensesForAppointments}
                            removeLicenseFromAppointment={removeLicenseFromAppointment}
                            appointmentLicenseColumnFields={appointmentLicenseColumnFields}
                        />
                    </TabPanel>
                );

            case 3:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <Inspection
                            appointment={appointmentDetail}
                            appointmentLicenses={appointmentLicenses}
                            data={appointmentInspectionsList}
                            loading={appointmentInspectionsLoading}
                            hasMore={hasMoreAppointmentInspections}
                            addInspection={addAppointmentInspection}
                            deleteInspection={deleteAppoinmentInspection}
                            skip={skipInspection}
                            setSkip={setSkipInspection}
                            setSortColumn={updateInspectionSortBy}
                            setSortOrder={updateInspectionSortOrder}
                            sortBy={appointmentInspectionSortColumn}
                            sortOrder={appointmentInspectionSortOrder}
                        />
                    </TabPanel>
                );

            case 4:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <AppointmentComment
                            appointmentDetails={appointmentDetail}
                            appointmentCommentList={appointmentCommentList}
                            addAppointmentComment={addAppointmentComment}
                            skip={skipComment}
                            setSkip={setSkipComment}
                            hasMore={hasMoreComment}
                            getAppointmentCommentsList={getAppointmentCommentsList}
                            loading={isAddAppointmentCommentLoading}
                        />
                    </TabPanel>
                );

            default:
                return <h1>Something went wrong. We are on it.</h1>;
        }
    };

    if (isLoading) return <Loader />;
    else if (!isLoading && !appointmentId) return <PageNotFound />;

    return (
        <div className="verticalFillContainer">
            <Header>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Title
                        titleText={`${appointmentDetail.type?.name} ${moment(appointmentDetail.startDateTime).format(
                            'L',
                        )}`}
                    />
                </Grid>
            </Header>
            <Box width="100%" display="flex" className="detail detail-mobile verticalFillContent">
                <DetailPageComponent
                    displayTabs={renderTabsPanel}
                    tabList={tabList}
                    onTabSelect={onTabSelect}
                ></DetailPageComponent>
            </Box>
        </div>
    );
};

export default AppointmentDetailTabs;
